// ═══════════════════════════════════════════════════════════════
// CORE ENUMS & TYPES
// ═══════════════════════════════════════════════════════════════

export type SoloLength = 4 | 8 | 12 | 16;

export type SoloInstrument = 
  | 'lead_guitar'
  | 'slide_guitar'
  | 'keys'
  | 'harmonica'
  | 'saxophone'
  | 'pedal_steel'
  | 'organ';

export const SOLO_INSTRUMENT_LABELS: Record<SoloInstrument, string> = {
  lead_guitar: 'Lead Guitar',
  slide_guitar: 'Slide Guitar',
  keys: 'Keys / Piano',
  harmonica: 'Harmonica',
  saxophone: 'Saxophone',
  pedal_steel: 'Pedal Steel',
  organ: 'Organ'
};

export type PhraseRole = 
  | 'entrance'
  | 'develop'
  | 'build'
  | 'peak'
  | 'resolve'
  | 'exit';

export const PHRASE_ROLE_LABELS: Record<PhraseRole, string> = {
  entrance: 'Entrance',
  develop: 'Develop',
  build: 'Build',
  peak: 'Peak',
  resolve: 'Resolve',
  exit: 'Exit'
};

export type TechniqueCategory =
  | 'sustain'
  | 'melodic'
  | 'rhythmic'
  | 'expressive'
  | 'technical'
  | 'atmospheric';

export type Register = 'low' | 'mid' | 'high' | 'climb' | 'descend';

export const REGISTER_LABELS: Record<Register, string> = {
  low: 'Low',
  mid: 'Mid',
  high: 'High',
  climb: 'Climbing',
  descend: 'Descending'
};

export type VibratoStyle = 'none' | 'subtle' | 'slow_wide' | 'fast_tight' | 'aggressive';

export const VIBRATO_LABELS: Record<VibratoStyle, string> = {
  none: 'None',
  subtle: 'Subtle',
  slow_wide: 'Slow & Wide',
  fast_tight: 'Fast & Tight',
  aggressive: 'Aggressive'
};

export type WahPosition = 'off' | 'half_cocked' | 'slow_sweep' | 'fast_sweep' | 'wacka_rhythm';

export const WAH_LABELS: Record<WahPosition, string> = {
  off: 'Off',
  half_cocked: 'Half-Cocked (Frozen)',
  slow_sweep: 'Slow Sweep',
  fast_sweep: 'Fast Sweep',
  wacka_rhythm: 'Rhythmic Wacka'
};

export type EffectEmphasis = 'none' | 'delay_trails' | 'reverb_wash' | 'feedback_bloom' | 'volume_swell';

export const EFFECT_LABELS: Record<EffectEmphasis, string> = {
  none: 'None',
  delay_trails: 'Delay Trails',
  reverb_wash: 'Reverb Wash',
  feedback_bloom: 'Feedback Bloom',
  volume_swell: 'Volume Swell'
};

export type BandInstruction = 
  | 'full_groove'
  | 'half_time'
  | 'drops_out'
  | 'drums_only'
  | 'bass_and_drums'
  | 'builds_with_solo';

export const BAND_INSTRUCTION_LABELS: Record<BandInstruction, string> = {
  full_groove: 'Full Groove',
  half_time: 'Half-Time Feel',
  drops_out: 'Band Drops Out',
  drums_only: 'Drums Only',
  bass_and_drums: 'Bass & Drums Only',
  builds_with_solo: 'Builds With Solo'
};

// ═══════════════════════════════════════════════════════════════
// TECHNIQUE
// ═══════════════════════════════════════════════════════════════

export interface Technique {
  id: string;
  name: string;
  category: TechniqueCategory;
  description: string;
  outputPhrase: string;
}

// ═══════════════════════════════════════════════════════════════
// SOLO PHRASE
// ═══════════════════════════════════════════════════════════════

export interface SoloPhrase {
  id: string;
  barRange: string;           // "1-2", "3-4", etc.
  name: string;               // User-editable name
  role: PhraseRole;
  
  // Core content
  techniqueId: string | null;
  register: Register;
  intensity: number;          // 0-100
  
  // Additions
  vibrato: VibratoStyle;
  effects: EffectEmphasis;
  wah: WahPosition;
  bandInstruction: BandInstruction;
  
  // Custom text
  customDescription: string;
}

// ═══════════════════════════════════════════════════════════════
// SOLO CONFIG
// ═══════════════════════════════════════════════════════════════

export interface SoloConfig {
  enabled: boolean;
  length: SoloLength;
  instrument: SoloInstrument;
  phrases: SoloPhrase[];
}

// ═══════════════════════════════════════════════════════════════
// SOLO OUTPUT
// ═══════════════════════════════════════════════════════════════

export interface SoloOutput {
  layerBlock: string;         // Full {LAYER-6 SOLO CHOREOGRAPHY} block
  compactSummary: string;     // One-line summary
  sectionHeader: string;      // "[Guitar Solo]" or "[Keys Solo]" etc.
}

// ═══════════════════════════════════════════════════════════════
// TEMPLATES
// ═══════════════════════════════════════════════════════════════

export interface SoloTemplate {
  id: string;
  name: string;
  description: string;
  length: SoloLength;
  defaultInstrument: SoloInstrument;
  phrases: Omit<SoloPhrase, 'id'>[];
}

// ═══════════════════════════════════════════════════════════════
// CONTEXT STATE
// ═══════════════════════════════════════════════════════════════

export interface SoloChoreographerState {
  config: SoloConfig;
  selectedPhraseIndex: number;
  activeTemplateId: string | null;
}

export interface SoloChoreographerContextType {
  state: SoloChoreographerState;
  output: SoloOutput | null;
  
  // Config actions
  setEnabled: (enabled: boolean) => void;
  setLength: (length: SoloLength) => void;
  setInstrument: (instrument: SoloInstrument) => void;
  
  // Phrase actions
  selectPhrase: (index: number) => void;
  updatePhrase: (index: number, updates: Partial<SoloPhrase>) => void;
  
  // Template actions
  applyTemplate: (templateId: string) => void;
  clearToCustom: () => void;
  
  // Output
  getSoloBlock: () => string;
  getSectionHeader: () => string;
  
  // Integration
  applySoloToLyrics: (lyrics: string) => string;
  
  // Reset
  clearSolo: () => void;
}
